import type { Match, Team, TeamId } from '@/core/schemas';
import type { PhaseView } from '@/features/standings/useTournamentView';
import { activeMatches, scoreLabel, sideLabel } from '@/features/matches/matchPresentation';

interface PanelProps {
  matches: readonly Match[];
  phases: readonly PhaseView[];
  lookup: (teamId: TeamId) => Team | undefined;
  boardCount: number;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex h-full flex-col gap-6">
      <h2 className="text-brand text-3xl font-bold tracking-widest uppercase">{title}</h2>
      {children}
    </section>
  );
}

/** Matches currently on a board — the panel the announcer points at most. */
export function ActiveMatchesPanel({ matches, lookup }: PanelProps) {
  const active = activeMatches(matches);

  return (
    <Panel title="Aktuelle Spiele">
      {active.length === 0 ? (
        <p className="text-fg-muted text-2xl">Derzeit läuft kein Spiel.</p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {active.map((match) => (
            <li key={match.id} className="border-line bg-surface rounded-2xl border p-6">
              <p className="text-fg-subtle text-xl">Board {match.boardNumber ?? '—'}</p>
              <p className="mt-2 text-4xl font-bold">{sideLabel(match.home, lookup)}</p>
              <p className="text-fg-muted my-1 text-2xl">
                {scoreLabel(match) === '' ? 'gegen' : scoreLabel(match)}
              </p>
              <p className="text-4xl font-bold">{sideLabel(match.away, lookup)}</p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function NextMatchesPanel({ matches, lookup }: PanelProps) {
  const upcoming = matches
    .filter((match) => match.status === 'pending')
    .filter((match) => match.home.kind === 'team' && match.away.kind === 'team')
    .slice(0, 8);

  return (
    <Panel title="Nächste Spiele">
      {upcoming.length === 0 ? (
        <p className="text-fg-muted text-2xl">Keine weiteren Spiele angesetzt.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {upcoming.map((match) => (
            <li
              key={match.id}
              className="border-line flex items-center justify-between border-b pb-3 text-3xl"
            >
              <span>{sideLabel(match.home, lookup)}</span>
              <span className="text-fg-subtle px-4 text-2xl">gegen</span>
              <span className="text-right">{sideLabel(match.away, lookup)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function GroupsPanel({ phases, lookup }: PanelProps) {
  const tables = phases.flatMap((phase) => phase.standings);

  return (
    <Panel title="Gruppen">
      <div className="grid gap-6 lg:grid-cols-2">
        {tables.map((table) => (
          <div
            key={table.groupId ?? table.label}
            className="border-line bg-surface rounded-2xl border p-5"
          >
            <h3 className="mb-3 text-2xl font-semibold">{table.label}</h3>
            <ol className="flex flex-col gap-1 text-2xl">
              {table.rows.map((row) => (
                <li key={row.teamId} className="flex justify-between">
                  <span>
                    <span className="text-fg-subtle mr-3">{row.rank}.</span>
                    {lookup(row.teamId)?.name ?? row.teamId}
                  </span>
                  <span className="font-semibold">{row.points}</span>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function BoardsPanel({ matches, lookup, boardCount }: PanelProps) {
  const byBoard = new Map(
    activeMatches(matches)
      .filter((match) => match.boardNumber !== null)
      .map((match) => [match.boardNumber, match]),
  );

  return (
    <Panel title="Boardbelegung">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: boardCount }, (_, index) => index + 1).map((number) => {
          const match = byBoard.get(number);
          return (
            <div
              key={number}
              className={
                match === undefined
                  ? 'border-line bg-surface rounded-2xl border p-5'
                  : 'border-info/40 bg-info-subtle rounded-2xl border p-5'
              }
            >
              <p className="text-fg-subtle text-xl">Board {number}</p>
              <p className="mt-1 text-2xl font-semibold">
                {match === undefined
                  ? 'frei'
                  : `${sideLabel(match.home, lookup)} / ${sideLabel(match.away, lookup)}`}
              </p>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
